import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { UrlService } from '@core/services/url/url.service';
import { FileDownloadService } from '@core/services/download/file-download.service';
import { FileSignatureManagerService } from '@core/services/files/file-signature-manager.service';
import { Subscription, combineLatest } from 'rxjs';
import { first } from 'rxjs/operators';

// import { AppService } from '@app/services/app/app.service';
// import { WorkflowsService } from '@app/services/settings/workflows.service';
// import { VersionsService } from '@app/services/app/versions.service';
import { AppRouterService } from 'app/services/app/router.service';
import { AppService } from 'app/services/app/app.service';

import * as Objects from '@core/objects';

import { ProjectsService } from '@app/services/app/projects.service';
import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { AmsFilesListItemMenuClick } from '@core/components/files/lists/files-list-item-menu-click';
import { NotificationsContextEnum } from '@core/store/models/notifications.enum';

import { TitleService } from '@core/services/title.service';
import { NotificationsService } from '@core/services/notifications/notifications.service';
import { AccountSession } from '@core/objects/account-session';

@Component({
    selector: 'project-preview-notes-main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss']
})
export class ProjectPreviewNotesMainComponent implements OnInit, OnDestroy{
    protected readonly PLUGIN_SEND_FILES = 'share_transfer';
    protected readonly APP_FILECOMPARISON = 'filecomparison';
    protected readonly ACCESS_GLOBAL_XRAY = 'access_global_xray';
    protected readonly PLUGIN_APOLLO = 'apollo'; // transitional: also gate "Compare" on this until apollo is fully retired

    public readonly STATUS_LOADING = 0;
    public readonly STATUS_DONE = 1;
    public readonly STATUS_NOT_ALLOWED = 2;

    public status: number = 0;
    public file: Objects.AmsFile = null;
    public project: Objects.Request = null;
    public notesUrlPrefix: string = '';
    public user: Objects.User = null;
    public canCreateNotes: boolean = true;
    public pluginsAccess: Array<string> = [];
    public appsAccess: Array<string> = [];
    public accessGlobalXray: boolean = true;
    public notes: Array<Objects.AnnotationsCounter> = [];

    protected urlSubscriptor: Subscription;
    protected filesSubscriptor: Subscription;
    protected projectSubscriptor: Subscription;
    protected appSubscriptor: Subscription;

    public breadcrumbs: any = [
        {
            title: "",
            link: ['']
        }
    ];

    public menu: Array<ThreeDotsMenuItem> = [
        {
            title: "Download",
            key: "download",
            icon: "download",
            order: 1
        }
    ];
    protected menuSendFiles: ThreeDotsMenuItem = {
        title: "Send",
        key: "send-files",
        icon: "send-files",
        order: 2
    };
    protected menuCompare: ThreeDotsMenuItem = {
        title: "Compare",
        key: "compare",
        icon: "compare",
        order: 3
    };
    protected menuComparison: ThreeDotsMenuItem = {
        title: "NEW Filecomparison",
        htmltitle: "<strong>NEW</strong> Filecomparison",
        key: "comparison",
        icon: "compare",
        order: 4
    };
    public menuPdfWithComments: ThreeDotsMenuItem = {
        title: "Download with comments",
        key: "download-comments",
        icon: "download_comments",
        order: 5
    };
    protected projectNotesSubscriptor: Subscription;


    constructor (private app: AppService,
        private title: TitleService,
        private activeRoute: ActivatedRoute,
        private projectsSrv: ProjectsService,
        private urlSrv: UrlService,
        private fileDownloadSrv: FileDownloadService,
        private signatureManager: FileSignatureManagerService,
        private notifSrv: NotificationsService,
        private appRouter: AppRouterService){}

    ngOnInit(): void {
        this.title.set("Project #");

        this.appSubscriptor = combineLatest([this.app.getSession(), this.app.getAccountSession()]).subscribe(([session, accountSession]: [any, AccountSession]) => {
            this.user = session.current.user;
            this.pluginsAccess = session.options.plugins;
            // apps list now comes from the accounts-api session; fall back to the legacy session field
            // in case the (separate, parallel) accounts-api call hasn't resolved yet
            this.appsAccess = accountSession?.current?.apps ?? session.options.organization.apps;
            this.accessGlobalXray = session.options.access?.hasOwnProperty(this.ACCESS_GLOBAL_XRAY) ? session.options.access[this.ACCESS_GLOBAL_XRAY] : true;
            this.updateMenu(this.menuSendFiles,this.checkPluginAccess(this.PLUGIN_SEND_FILES))
            this.updateMenu(this.menuCompare,this.checkCompareAccess());
            this.updateMenu(this.menuComparison,this.checkFilecomparisonAccess());
        });

        this.projectSubscriptor = this.projectsSrv.selectProject().subscribe((project: Objects.Request) => {
            if(project == null) return;

            this.setProject(project);
        });

        this.projectNotesSubscriptor = this.projectsSrv.selectProjectFilesNotesCounter().subscribe((notes: Objects.AnnotationsCounter[] ) => {
            if(notes != null){
                this.notes = notes;
                this.init()
            }
        });

        this.urlSubscriptor = this.activeRoute.params.subscribe((params: any) => {
            // console.log(" params ", params);
            let fid: any = +params.fid;
            this.filesSubscriptor = this.projectsSrv.selectProjectFiles().subscribe((files: Objects.AmsFile[] ) => {
                if(files.length > 0){
                    this.file = files.filter((f: Objects.AmsFile) => this.filesMatch(f, fid))[0];
                    // File must be loaded on the project store                  
                    this.init();
                }
            });
        });


    }

    ngOnDestroy() {
        this.urlSubscriptor.unsubscribe();
        this.projectSubscriptor.unsubscribe();
        this.appSubscriptor.unsubscribe();
        this.projectNotesSubscriptor.unsubscribe();
        if(this.filesSubscriptor)
            this.filesSubscriptor.unsubscribe();
    }

    init() : void{
        if( this.notes != null && this.file != null){
            this.updateMenu(this.menuPdfWithComments,this.getNotes(this.file) > 0)
            this.status = this.STATUS_DONE;
        }
    }


    onNotesSaved(event: boolean){
        this.projectsSrv.loadFilesNotesCounter();
    }

    protected setProject(project:  Objects.Request) {
        // console.log(" PROJECT GOT ",project);
        this.updateBreadcrumbs(project);
        this.notesUrlPrefix = '/requests/'+project.id;
        this.project = project;
        this.title.set("Preview & Notes - Project #"+this.project.id);
    }

    protected updateBreadcrumbs(project: Objects.Request){
        this.breadcrumbs[0].link = '/projects/'+project.id;
        this.breadcrumbs[0].title = 'Project <small>(#'+project.id+ ')</small>';
    }

    onMenuClick(menu: AmsFilesListItemMenuClick) {
        switch(menu.key) {
            case "download-comments": 
                // start notification to download the pdf with the annotations in it. We need the url for the prefix of the download and the file.
                this.notifSrv.addNotification(NotificationsContextEnum.EVENT_FILE_ANNOTATIONS_START, {id: this.project.id, details: { url: "/requests/"+this.project.id, amsfile: menu.file}});
                break;
            case "download":
                this.handleFileDownload(menu.file, {disposition: 'attachment'});
                break;
            case "send-files":
                if (this.checkPluginAccess(this.PLUGIN_SEND_FILES))
                    this.appRouter.navigate('/p/transfer/'+this.project.id, { queryParams: { file: +menu.file.id} });
                break;
            case "compare":
                this.appRouter.navigate('/p/apollo/request/'+this.project.id);
                break;
            case "comparison":
                this.appRouter.navigate('/filecomparison/project/'+this.project.id+'/file/'+menu.file.uuid);
                break;
        }
    }

    onActionDownloadClick(file: Objects.AmsFile){  
        this.handleFileDownload(file, {disposition: 'attachment'});
    }

    public onRefreshSignaturesNeeded(e: any) {
        this.projectsSrv.loadAttachments();
    }

    protected checkPluginAccess(plugin: string){
        return this.pluginsAccess.some((pa) => pa == plugin);
    }

    protected checkAppAccess(app: string){
        return this.appsAccess.some((a) => a == app);
    }

    protected checkCompareAccess(): boolean {
        return this.accessGlobalXray && this.checkPluginAccess(this.PLUGIN_APOLLO);
    }

    protected checkFilecomparisonAccess(): boolean {
        return this.accessGlobalXray && this.checkAppAccess(this.APP_FILECOMPARISON);
    }

    // this function check either remove or add item. based on subscribed events
    protected updateMenu(item: ThreeDotsMenuItem, addItem:boolean = true) {
        this.menu = this.menu.filter((i: ThreeDotsMenuItem) => item.key != i.key);
        if(addItem){
            this.menu.push(item);
        }
    }

    /**
     * Compare files by UUID if present, otherwise by ID
     * Future-proofed for UUID-based file identification
     */
    private filesMatch(file: Objects.AmsFile, compareWith: Objects.AmsFile | number): boolean {
        if (!file) return false;
        
        // If comparing with another file object, use UUID if both have it
        if (typeof compareWith === 'object') {
            if (file.uuid && compareWith.uuid) {
                return file.uuid === compareWith.uuid;
            }
            return file.id === compareWith.id;
        }
        
        // If comparing with a number (ID), use ID comparison
        return file.id === compareWith;
    }

    protected getNotes(file: Objects.AmsFile){
        return this.notes.filter((n: Objects.AnnotationsCounter) => n.file.id == file.id)[0]?.annotations?.count;
    }

    /**
     * Handle file download with signature expiration check
     * If signature is expired, refresh files and retry download
     */
    private handleFileDownload(file: Objects.AmsFile, options?: any) {
        if (file && file.access && this.signatureManager.isSignatureExpired(file)) {
            // Signature expired - refresh files and wait for fresh signatures
            this.projectsSrv.loadAttachments();
            // Wait for store to have a file with fresh signature before downloading
            // Use predicate to ensure we get a file with valid signature
            this.projectsSrv.selectProjectFiles().pipe(
                first((files: Objects.AmsFile[]) => {
                    // Ensure we have the file and its signature is fresh
                    const updatedFile = files?.find((f: Objects.AmsFile) => this.filesMatch(f, file));
                    return updatedFile && !this.signatureManager.isSignatureExpired(updatedFile);
                })
            ).subscribe((files: Objects.AmsFile[]) => {
                const updatedFile = files.find((f: Objects.AmsFile) => this.filesMatch(f, file));
                if (updatedFile) {
                    this.fileDownloadSrv.downloadFile(updatedFile, options);
                } else {
                    // Fallback if file not found after refresh
                    window.location.href = this.urlSrv.build('/files/'+file.id);
                }
            });
        } else if (file && file.access) {
            // Signature is valid, use the download service
            this.fileDownloadSrv.downloadFile(file, options);
        } else {
            // No signature, fall back to legacy download
            window.location.href = this.urlSrv.build('/files/'+file.id);
        }
    }


}
